import { Field, Listbox, ListboxButton, ListboxOptions } from "@headlessui/react";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/utils/css";
import { TranslatedText } from "@/components/common/text";
import { useMemo } from "react";
import { kmaToMarket } from "@/utils/markets";

export const MultiSelectDropdown = ({ items = {}, value = {}, setValue, label, className, placeholder = "Select", minWidth = 239 }) => {
    const pxMinWidth = `${minWidth}px`;

    const handleToggle = (itemId) => {
        const newValue = { ...value };
        if (newValue[itemId]) {
            delete newValue[itemId];
        } else {
            newValue[itemId] = true;
        }
        setValue(newValue);
    };

    const itemsLength = useMemo(() => {
        return items ? Object.keys(items).length : 0;
    }, [items]);

    const getDisplayText = () => {
        const selectedKeys = Object.keys(value).filter((key) => value[key]);
        if (selectedKeys.length === 0) {
            return placeholder;
        }
        const selectedItems = Object.keys(items).filter((key) => value[key]);
        if (selectedItems.length === 1) {
            return kmaToMarket[selectedItems[0]] || selectedItems[0];
        }
        return `${kmaToMarket[selectedItems[0]] || selectedItems[0]} and ${selectedItems.length - 1} other`;
    };

    return (
        <div className="flex flex-row gap-[10px] items-center">
            <TranslatedText className="text-sm font-normal">{label}</TranslatedText>
            <Field
                className={cn("flex flex-col bg-cm-p-surface-container h-[38px] px-4 py-[10px] rounded-[58px]", className)}
                style={{ minWidth: pxMinWidth }}
            >
                <Listbox value={null} onChange={() => {}}>
                    {({ open }) => (
                        <>
                            <ListboxButton className="inline-flex flex-row justify-between items-center gap-1">
                                <span
                                    className={`text-sm ${Object.keys(value).some((key) => value[key]) ? "text-cm-p-text-primary font-medium" : "text-cm-text-tertiary"} `}
                                >
                                    {getDisplayText()}
                                </span>
                                <ChevronDown
                                    className={cn("opacity-50 text-cm-text-primary transition ease-in w-4 h-4", {
                                        "rotate-180": open,
                                    })}
                                />
                            </ListboxButton>
                            <ListboxOptions
                                portal={false}
                                modal={true}
                                className="z-40 flex flex-col mt-[18px] rounded-lg bg-cm-p-surface py-[2px] px-1 w-[calc(100%+32px)] -ml-4 border border-cm-p-outline-variation max-h-[305px] h-auto overflow-auto gap-[2px]"
                                style={{
                                    boxShadow: "0px 4px 14px -3px rgba(93, 93, 93, 0.08)",
                                    minHeight: `${(itemsLength > 5 ? 5 : itemsLength) * 43 + 4}px`,
                                }}
                            >
                                {Object.keys(items).map((key) => {
                                    const count = items[key];
                                    const isSelected = !!value[key];

                                    return (
                                        <div
                                            key={key}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleToggle(key);
                                            }}
                                            className="inline-flex min-h-[41px] px-4 items-center cursor-pointer justify-between hover:bg-cm-p-surface-hover rounded-lg "
                                        >
                                            <span className="text-cm-p-text-primary font-medium text-sm">
                                                {kmaToMarket[key] || key} ({count})
                                            </span>
                                            <div
                                                className={cn(
                                                    "w-4 h-4 border-2 rounded flex items-center justify-center border-cm-p-outline",
                                                    { "border-cm-p-accent-500 bg-cm-p-accent-500": isSelected },
                                                )}
                                            >
                                                {isSelected && <Check className="size-3 text-white" />}
                                            </div>
                                        </div>
                                    );
                                })}
                            </ListboxOptions>
                        </>
                    )}
                </Listbox>
            </Field>
        </div>
    );
};
